import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GlossaryText from "../components/GlossaryText";
import api, { downloadBlob, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Empty, EnforcementBadge, AccessBadge, ReservedBadge } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RequirementHover } from "../components/RequirementHover";
import { standardRegion } from "../lib/constants";
import { CaretRight, Info, DownloadSimple, MagnifyingGlass, X } from "@phosphor-icons/react";
import ApplicabilityScopingCard from "../components/ApplicabilityScopingCard";
import StandardsCoverageSummary from "../components/StandardsCoverageSummary";

const FILTERS = [["all", "All"], ["currently_enforceable", "Enforceable"], ["future_enforceable", "Future"], ["retired", "Retired"]];

// Friendly labels for NERC functional registrations
const FUNC_LABELS = {
  BA: "Balancing Authority", DP: "Distribution Provider", "DP-UFLS": "DP — UFLS-only",
  FRSG: "Frequency Response Sharing Group", GO: "Generator Owner", "GO-2": "Generator Owner (2)",
  GOP: "Generator Operator", "GOP-2": "Generator Operator (2)", IA: "Interchange Authority",
  LSE: "Load-Serving Entity", PA: "Planning Authority", PC: "Planning Coordinator",
  PSE: "Purchasing-Selling Entity", RC: "Reliability Coordinator", RE: "Regional Entity",
  RP: "Resource Planner", RRO: "Regional Reliability Organization", RRSG: "Regulation Reserve Sharing Group",
  RSG: "Reserve Sharing Group", TO: "Transmission Owner", TOP: "Transmission Operator",
  TP: "Transmission Planner", TSP: "Transmission Service Provider",
  CLO: "Computational Load Owner", CLOP: "Computational Load Operator",
};
const funcLabel = (c) => (FUNC_LABELS[c] ? `${c} — ${FUNC_LABELS[c]}` : c);
const CFR_CHIP = {
  sole: { label: "Sole", full: "Sole responsibility", cls: "border-sky-300 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
  shared_both: { label: "Shared", full: "Shared — both parties must perform", cls: "border-violet-300 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300" },
  split: { label: "Split", full: "Split — each party performs part", cls: "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
  not_applicable: { label: "N/A", full: "Not applicable under the coordinated registration", cls: "border-gray-300 bg-gray-50 text-gray-500 dark:bg-gray-900/40 dark:text-gray-400" },
  unspecified: { label: "—", full: "Responsibility not specified", cls: "border-gray-300 bg-gray-50 text-gray-500 dark:bg-gray-900/40 dark:text-gray-400" },
};

export default function Requirements() {
  const [standards, setStandards] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [regFuncs, setRegFuncs] = useState([]);
  const [regRegions, setRegRegions] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [func, setFunc] = useState("all");
  const [scope, setScope] = useState("mine"); // "mine" | "all"
  const [smeFilter, setSmeFilter] = useState("any");   // "any" | "unassigned" | <person name>
  const [clFilter, setClFilter] = useState("any");     // "any" | "unassigned" | <person name>
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/standards"), api.get("/requirements"), api.get("/functions"), api.get("/entity-profile")])
      .then(([s, r, f, p]) => {
        setStandards(s.data); setReqs(r.data); setFunctions(f.data);
        const rf = p.data?.registered_functions || [];
        setRegFuncs(rf);
        setRegRegions(p.data?.regional_entities || []);
        // If the entity has no registered functions yet, default to showing all.
        if (!rf.length) setScope("all");
      });
  }, []);

  const hasProfile = regFuncs.length > 0;
  // Functions available in the dropdown depend on the active scope.
  const dropdownFuncs = scope === "mine" && hasProfile
    ? functions.filter((c) => regFuncs.includes(c))
    : functions;

  // A regional standard is in scope only when its region is selected; when no
  // regions are selected, region filtering is off (show all). Continent-wide always in.
  const regionInScope = (r) => {
    const region = standardRegion(r.standard_id);
    if (!region || !regRegions.length) return true;
    return regRegions.includes(region);
  };

  // Is a requirement in scope given scope toggle + per-function dropdown?
  // "My registration" honors the backend's full applicability verdict
  // (registered functions + CIP BES Cyber System impact ratings + region +
  // applicability answers), so e.g. a Low-impact entity does NOT see High/Medium-only
  // CIP requirements. "Show all" lists everything.
  const inScope = (r) => {
    const rf = r.applicable_functions || [];
    if (func !== "all") return rf.includes(func) && regionInScope(r);
    if (scope === "mine" && hasProfile) return r.applicable === true;
    return true;
  };

  const reqsFor = (sid) => reqs.filter((r) => r.standard_id === sid && inScope(r) && matchesSearch(r) && matchesPerson(r));

  // Person-based filter: SME or Compliance Lead assignment (primary OR backup).
  // "any" = no filter, "unassigned" = requirement has no assignment for that role.
  const matchesPerson = (r) => {
    // SME
    if (smeFilter !== "any") {
      const smes = [r.sme_primary, r.sme_backup].filter(Boolean);
      if (smeFilter === "unassigned") { if (smes.length > 0) return false; }
      else if (!smes.includes(smeFilter)) return false;
    }
    // Compliance Lead
    if (clFilter !== "any") {
      const cls = [r.compliance_primary, r.compliance_backup].filter(Boolean);
      if (clFilter === "unassigned") { if (cls.length > 0) return false; }
      else if (!cls.includes(clFilter)) return false;
    }
    return true;
  };

  // Distinct people assigned across all requirements (for dropdowns).
  const peopleOptions = useMemo(() => {
    const smes = new Set(), cls = new Set();
    for (const r of reqs) {
      [r.sme_primary, r.sme_backup].forEach((v) => { if (v) smes.add(v); });
      [r.compliance_primary, r.compliance_backup].forEach((v) => { if (v) cls.add(v); });
    }
    return { smes: [...smes].sort(), cls: [...cls].sort() };
  }, [reqs]);

  // Free-text search across standard id/title, requirement number/text and subparts.
  const ql = q.trim().toLowerCase();
  const stdTitleById = useMemo(() => Object.fromEntries(standards.map((s) => [s.standard_id, s.title || ""])), [standards]);
  const matchesSearch = (r) => {
    if (!ql) return true;
    const fields = [r.standard_id, r.req_number, r.text, r.title, stdTitleById[r.standard_id]];
    if (fields.some((f) => String(f || "").toLowerCase().includes(ql))) return true;
    return (r.sub_requirements || []).some((sp) => `${sp.label || ""} ${sp.text || ""}`.toLowerCase().includes(ql));
  };

  const visible = useMemo(() => {
    let list = filter === "all" ? standards : standards.filter((s) => (s.status || "currently_enforceable") === filter);
    list = list.filter((s) => reqsFor(s.standard_id).length > 0);
    return list;
  }, [standards, reqs, filter, func, scope, regFuncs, regRegions, q, smeFilter, clFilter]);

  const setScopeSafe = (v) => { setScope(v); setFunc("all"); };

  // ---- Export options (independent of the on-screen filters) ----
  const [exportScope, setExportScope] = useState("all"); // "all" | "applicable"
  const [exportStatuses, setExportStatuses] = useState({
    currently_enforceable: true, future_enforceable: true, retired: true,
  });
  const toggleExportStatus = (k) => setExportStatuses((s) => ({ ...s, [k]: !s[k] }));
  const exportScopeOptions = [
    ["all", "All NERC requirements"],
    ["applicable", `Only my registration's applicable requirements${hasProfile ? ` (${regFuncs.join(", ")})` : ""}`],
  ];
  const exportStatusOptions = [
    ["currently_enforceable", "Currently enforced"],
    ["future_enforceable", "Future enforcement"],
    ["retired", "Retired"],
  ];
  const runExport = async () => {
    const picked = Object.keys(exportStatuses).filter((k) => exportStatuses[k]);
    const params = new URLSearchParams();
    params.set("scope", exportScope);
    if (picked.length && picked.length < 3) params.set("statuses", picked.join(","));
    try {
      await downloadBlob(`/requirements/export?${params.toString()}`, "nerc-requirements.xlsx");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const anyStatus = Object.values(exportStatuses).some(Boolean);

  return (
    <div>
      <PageHeader title="NERC Requirements Library" subtitle="Standards, requirements, purpose & functional applicability"
        action={
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="export-requirements-btn" className="rounded-none bg-primary hover:bg-primary/90">
                <DownloadSimple size={16} className="mr-1.5" />Export to Excel
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-none p-0" data-testid="export-options-popover">
              <div className="border-b border-border px-4 py-3">
                <div className="font-display font-bold text-sm">Export to Excel</div>
                <div className="text-xs text-muted-foreground">Choose what to include in the spreadsheet.</div>
              </div>
              <div className="px-4 py-3 space-y-4">
                <div className="space-y-2">
                  <div className="overline text-muted-foreground">Scope</div>
                  {exportScopeOptions.map(([val, label]) => (
                    <label key={val} data-testid={`export-scope-${val}`}
                      className={`flex items-start gap-2 text-sm cursor-pointer ${val === "applicable" && !hasProfile ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <input type="radio" name="export-scope" value={val} checked={exportScope === val}
                        disabled={val === "applicable" && !hasProfile}
                        onChange={() => setExportScope(val)} className="mt-0.5 accent-primary" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="overline text-muted-foreground">Enforcement status</div>
                  {exportStatusOptions.map(([k, label]) => (
                    <label key={k} data-testid={`export-status-${k}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={exportStatuses[k]} onCheckedChange={() => toggleExportStatus(k)} className="rounded-none" />
                      <span>{label}</span>
                    </label>
                  ))}
                  {!anyStatus && <p className="text-xs text-amber-600">Select at least one status.</p>}
                </div>
                <Button data-testid="export-run-btn" onClick={runExport} disabled={!anyStatus}
                  className="w-full rounded-none bg-primary hover:bg-primary/90">
                  <DownloadSimple size={15} className="mr-1.5" />Download .xlsx
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        } />
      <div className="p-8 space-y-6">
        {/* Library coverage counts: full NERC set vs. entity's applicable subset */}
        <StandardsCoverageSummary />

        {/* Scope toggle: my registered functions vs. everything */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="overline text-muted-foreground mr-1">View</span>
          <div className="inline-flex border border-border">
            <button data-testid="scope-mine" onClick={() => setScopeSafe("mine")} disabled={!hasProfile}
              className={`px-3 py-1 text-xs font-semibold ${scope === "mine" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"} ${!hasProfile ? "opacity-40 cursor-not-allowed" : ""}`}>
              My registration{hasProfile ? ` (${regFuncs.length})` : ""}
            </button>
            <button data-testid="scope-all" onClick={() => setScopeSafe("all")}
              className={`px-3 py-1 text-xs font-semibold border-l border-border ${scope === "all" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
              Show all
            </button>
          </div>
        </div>

        {/* Contextual banner */}
        {scope === "mine" && hasProfile && (
          <div data-testid="scope-banner" className="flex items-start gap-2 border border-border bg-secondary/40 px-4 py-3 text-sm">
            <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
            <p className="text-muted-foreground">
              Scoped to your registered functions:{" "}
              <span className="font-mono text-foreground">{regFuncs.join(", ")}</span>.
              Whether a specific requirement actually applies may still depend on the assets you own —
              confirm applicability on each requirement.{" "}
              <button className="underline" onClick={() => setScopeSafe("all")}>Show all functions</button>.
            </p>
          </div>
        )}
        {!hasProfile && (
          <div data-testid="no-profile-banner" className="flex items-start gap-2 border border-border bg-secondary/40 px-4 py-3 text-sm">
            <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
            <p className="text-muted-foreground">
              No registered functions set, so all standards are shown.{" "}
              <button className="underline" onClick={() => nav("/admin")}>Set your Entity Profile</button> to scope this library to your registration.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="requirements-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search standards & requirements (e.g. CIP-007, R3, password, BAL-001)…"
              className="w-full border border-border bg-card pl-9 pr-9 py-2 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-primary" />
            {q && (
              <button data-testid="requirements-search-clear" onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={15} />
              </button>
            )}
          </div>
          {q && <span data-testid="requirements-search-count" className="text-xs text-muted-foreground">{visible.length} standard{visible.length === 1 ? "" : "s"} match</span>}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="overline text-muted-foreground mr-1">Status</span>
            {FILTERS.map(([v, l]) => (
              <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
                className={`border px-3 py-1 text-xs font-semibold ${filter === v ? "bg-primary text-primary-foreground border-black" : "border-border hover:bg-secondary"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="overline text-muted-foreground mr-1">Functional registration</span>
            <Select value={func} onValueChange={setFunc}>
              <SelectTrigger data-testid="function-filter" className="rounded-none h-8 w-64 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{scope === "mine" && hasProfile ? "All my functions" : "All functions"}</SelectItem>
                {dropdownFuncs.map((c) => <SelectItem key={c} value={c}>{funcLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            {func !== "all" && <button data-testid="clear-func" onClick={() => setFunc("all")} className="text-xs underline text-muted-foreground">clear</button>}
          </div>
          <div className="flex items-center gap-2">
            <span className="overline text-muted-foreground mr-1">SME</span>
            <Select value={smeFilter} onValueChange={setSmeFilter}>
              <SelectTrigger data-testid="sme-filter" className="rounded-none h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any SME</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {peopleOptions.smes.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            {smeFilter !== "any" && <button data-testid="clear-sme" onClick={() => setSmeFilter("any")} className="text-xs underline text-muted-foreground">clear</button>}
          </div>
          <div className="flex items-center gap-2">
            <span className="overline text-muted-foreground mr-1">Compliance Lead</span>
            <Select value={clFilter} onValueChange={setClFilter}>
              <SelectTrigger data-testid="cl-filter" className="rounded-none h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any CL</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {peopleOptions.cls.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            {clFilter !== "any" && <button data-testid="clear-cl" onClick={() => setClFilter("any")} className="text-xs underline text-muted-foreground">clear</button>}
          </div>
        </div>

        {/* Applicability scoping card — explains WHY certain standards apply/don't (before the breakdown) */}
        <ApplicabilityScopingCard />

        {visible.length === 0 && <Empty title="No matching standards" hint="Load standards from Admin → Standards Library, or change the filters." />}
        {visible.map((s) => {
          const list = reqsFor(s.standard_id);
          return (
            <div key={s.id} className="border border-border bg-card" data-testid={`standard-${s.standard_id}`}>
              <div className="border-b border-border px-5 py-4 bg-secondary/40">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{s.standard_id}</span>
                  <span className="font-display font-bold text-lg">{s.title || <span className="text-muted-foreground italic font-normal">untitled</span>}</span>
                  <EnforcementBadge status={s.status} effectiveDate={s.effective_date} />
                  <AccessBadge access={s.access} data-testid={`std-access-${s.standard_id}`} />
                </div>
                {s.purpose && (
                  <p className="text-sm text-muted-foreground mt-2" data-testid={`purpose-${s.standard_id}`}>
                    <span className="overline text-foreground mr-2">Purpose</span><GlossaryText text={s.purpose} />
                  </p>
                )}
              </div>
              <div className="divide-y divide-border">
                {list.map((r) => {
                  const gated = r.access?.gated;
                  return (
                  <button key={r.id} data-testid={`req-row-${r.req_number}`} onClick={() => nav(`/requirements/${r.id}`)}
                    className={`w-full text-left flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/50 ${gated || r.reserved ? "opacity-60" : ""}`}>
                    <RequirementHover label={`${s.standard_id} ${r.req_number}`} text={r.text}>
                      <span data-testid={`req-num-hover-${r.req_number}`} className="font-mono font-bold text-sm w-10 shrink-0 cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">{r.req_number}</span>
                    </RequirementHover>
                    <span className="flex-1 text-sm">
                      <GlossaryText text={r.text} />
                      {r.measure && (
                        <span data-testid={`req-list-measure-${r.req_number}`} className="block mt-1 text-xs text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 border-l-2 border-emerald-400 dark:border-emerald-600 pl-2 py-0.5">
                          <span className="font-bold uppercase text-[10px] mr-1 text-emerald-700 dark:text-emerald-300">Measure</span><GlossaryText text={r.measure} />
                        </span>
                      )}
                    </span>
                    {(r.sub_requirements || []).length > 0 && (
                      <span data-testid={`req-parts-count-${r.req_number}`} className="text-[10px] uppercase tracking-wide border border-border bg-secondary px-1.5 py-0.5 text-muted-foreground shrink-0">{r.sub_requirements.length} parts</span>
                    )}
                    {(() => {
                      const af = r.applicable_functions || [];
                      if (af.length === 0) return null;
                      const mine = scope === "mine" && hasProfile;
                      const shown = mine ? af.filter((f) => regFuncs.includes(f)) : af;
                      if (shown.length === 0) return null;
                      return (
                        <span data-testid={`req-functions-${r.standard_id}-${r.req_number}`}
                          title={mine ? "Your registered functions this requirement applies to" : "Functions this requirement applies to"}
                          className="text-[11px] text-muted-foreground font-mono hidden lg:inline">{shown.join(" · ")}</span>
                      );
                    })()}
                    {r.cfr_type && CFR_CHIP[r.cfr_type] && (
                      <span data-testid={`req-cfr-chip-${r.standard_id}-${r.req_number}`} title={`Coordinated Functional Registration: ${CFR_CHIP[r.cfr_type].full}`}
                        className={`text-[10px] uppercase tracking-wide font-bold border px-1.5 py-0.5 shrink-0 ${CFR_CHIP[r.cfr_type].cls}`}>
                        CFR · {CFR_CHIP[r.cfr_type].label}
                      </span>
                    )}
                    <AccessBadge access={r.access} data-testid={`req-access-${r.req_number}`} />
                    {r.reserved && <ReservedBadge data-testid={`req-reserved-${r.req_number}`} />}
                    {!r.access?.plan_locked && r.status === "future_enforceable" && <EnforcementBadge status={r.status} effectiveDate={r.effective_date} />}
                    <CaretRight size={16} className="text-muted-foreground" />
                  </button>
                  );
                })}
                {list.length === 0 && <div className="px-5 py-3 text-sm text-muted-foreground">No requirements yet — add them in Admin → Standards Library.</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
